import React from "react";
import { cx } from "utils/join-classnames";
import secondsToTimecode from "utils/secondsToTimecode";
import { combineTimestamps, formatCombinedTimestamp } from "core/models";
import styles from "./ScrubBar.module.scss";

interface Props {
    vidRef: React.RefObject<HTMLVideoElement | null>;
    size?: "small" | "medium" | "large";
    trim?: [number, number];
    showCurrentTime?: boolean;
    expanded?: boolean;
    selectionTimestamp?: string;
    inEditor?: boolean;
}
const ScrubBar: React.FC<Props> = ({
    vidRef,
    size = "medium",
    trim = [0, 0],
    showCurrentTime = false,
    expanded = false,
    selectionTimestamp,
    inEditor,
}) => {
    const animationFrame = React.useRef<number | null>(null);
    const animationFrameCount = React.useRef<number>(0);
    const progressRef = React.useRef<HTMLDivElement>(null);
    const thumbRef = React.useRef<HTMLDivElement>(null);
    const trackRef = React.useRef<HTMLDivElement>(null);
    const timecodeRef = React.useRef<HTMLDivElement>(null);
    const [dragging, setDragging] = React.useState<boolean>(false);
    const [showMouseTimecode, setShowMouseTimecode] =
        React.useState<boolean>(false);
    const [mouseTimecode, setMouseTimecode] = React.useState<string>("00:00");
    const [trimStart, trimEnd] = trim;
    const playingRef = React.useRef<boolean>(false);
    const trimRef = React.useRef<[number, number]>([0, 0]);
    const [currentTime, setCurrentTime] = React.useState<number>(0);

    const combinedTimestamp = combineTimestamps(
        selectionTimestamp || "00:00:00",
        mouseTimecode,
    );

    const formattedCombinedTimestamp =
        formatCombinedTimestamp(combinedTimestamp);

    const calculateProgressWidth = (): void => {
        const trackWidth = trackRef.current?.offsetWidth || 0;
        if (
            !vidRef.current ||
            !progressRef.current ||
            !trackWidth ||
            !trimRef
        ) {
            return;
        }
        const [trimStartRef, trimEndRef] = trimRef.current;
        const calculatedTime = vidRef.current.currentTime - trimStartRef;
        const calculatedDuration =
            vidRef.current.duration - (trimStartRef + trimEndRef);
        const currentProgress =
            Math.round(calculatedTime * 100) /
            Math.round(calculatedDuration * 100);
        let calculatedWidth = trackWidth * currentProgress;
        if (calculatedWidth > trackWidth) {
            calculatedWidth = trackWidth;
        }
        progressRef.current.style.width = `${calculatedWidth}px`;
        if (showCurrentTime && animationFrameCount.current % 10 === 0) {
            setCurrentTime(vidRef.current.currentTime);
        }
        animationFrameCount.current += 1;
    };

    const updateTimeOnce = () => {
        calculateProgressWidth();
        if (vidRef.current) {
            setCurrentTime(vidRef.current.currentTime);
        }
    };
    const updateTime = () => {
        calculateProgressWidth();
    };

    const onResize = () => {
        calculateProgressWidth();
    };

    const handleTimeUpdate = (): void => {
        if (playingRef.current) {
            return;
        }
        updateTimeOnce();
    };

    const animate = () => {
        if (playingRef.current) {
            updateTime();
        }
        animationFrame.current = requestAnimationFrame(animate);
    };

    const onPlay = () => {
        playingRef.current = true;
    };
    const onPause = () => {
        playingRef.current = false;
    };
    React.useEffect(() => {
        trimRef.current = trim;
        updateTimeOnce();
    }, [trim]);

    React.useEffect(() => {
        animationFrame.current = requestAnimationFrame(animate);
        window.addEventListener("resize", onResize);
        return () => {
            if (animationFrame.current) {
                cancelAnimationFrame(animationFrame.current);
            }
            window.removeEventListener("resize", onResize);
        };
    }, []);

    const handleTrackEnter = () => {
        setShowMouseTimecode(true);
    };
    const handleTrackLeave = () => {
        setShowMouseTimecode(false);
    };

    const onMouseUp = React.useCallback(
        (e: MouseEvent) => {
            e.preventDefault();
            setDragging(false);
        },
        [animationFrame.current],
    );

    const scrubBasedOnMouseClientX = React.useCallback(
        (clientX: number) => {
            if (!trackRef || !vidRef.current) {
                return;
            }
            const trackWidth = trackRef.current?.offsetWidth || 0;
            const calculatedDuration =
                vidRef.current.duration - (trimStart + trimEnd);
            const leftPos = trackRef.current?.getBoundingClientRect().left || 0;
            const aimedPercent = (clientX - leftPos) / trackWidth;
            const aimedSecond = calculatedDuration * aimedPercent;
            // eslint-disable-next-line no-param-reassign
            vidRef.current.currentTime = aimedSecond + trimStart;
        },
        [trackRef, vidRef, trimStart, trimEnd],
    );
    const onMouseMove = React.useCallback(
        (e: MouseEvent) => {
            if (!dragging || !thumbRef) {
                return;
            }
            scrubBasedOnMouseClientX(e.clientX);
            updateTimeOnce();
        },
        [dragging, scrubBasedOnMouseClientX, updateTimeOnce],
    );
    const handleGrabThumb = (e: React.MouseEvent) => {
        e.preventDefault();
        setDragging(true);
        playingRef.current ? onPause() : null;
    };

    const handleReleaseThumb = (e: React.MouseEvent) => {
        e.preventDefault();
        setDragging(false);
        updateTimeOnce();
    };
    const handleTrackClick = (e: React.MouseEvent) => {
        e.preventDefault();
        scrubBasedOnMouseClientX(e.clientX);
        updateTimeOnce();
    };

    const onMoveTimecode = (e: MouseEvent): void => {
        if (!timecodeRef.current || !trackRef.current || !vidRef.current) {
            return;
        }
        e.preventDefault();
        const trackWidth = trackRef.current.offsetWidth || 0;
        const leftPos = trackRef.current.getBoundingClientRect().left;
        timecodeRef.current.style.left = `${e.clientX - leftPos}px`;
        const calculatedDuration =
            vidRef.current.duration - (trimStart + trimEnd);
        const aimedPercent = ((e.clientX - leftPos) / trackWidth) * 100;
        const aimedSecond = (calculatedDuration / 100) * aimedPercent;
        if (aimedSecond) {
            setMouseTimecode(secondsToTimecode(aimedSecond));
        }
    };

    React.useEffect(() => {
        if (!dragging) {
            return undefined;
        }
        document.body.addEventListener("mousemove", onMouseMove);
        document.body.addEventListener("mouseup", onMouseUp);
        return () => {
            document.body.removeEventListener("mousemove", onMouseMove);
            document.body.removeEventListener("mouseup", onMouseUp);
        };
    }, [dragging, onMouseMove, onMouseUp]);

    React.useEffect(() => {
        if (!showMouseTimecode) {
            return undefined;
        }
        document.body.addEventListener("mousemove", onMoveTimecode);
        return () =>
            document.body.removeEventListener("mousemove", onMoveTimecode);
    }, [showMouseTimecode]);

    React.useEffect(() => {
        const videoPlayerRef = vidRef.current;
        if (!videoPlayerRef) {
            return undefined;
        }
        videoPlayerRef.addEventListener("play", onPlay);
        videoPlayerRef.addEventListener("pause", onPause);
        videoPlayerRef.addEventListener("paused", onPause);
        if (vidRef.current) {
            vidRef.current.addEventListener("timeupdate", handleTimeUpdate, {
                passive: true,
            });
        }
        return () => {
            videoPlayerRef.removeEventListener("play", onPlay);
            videoPlayerRef.removeEventListener("pause", onPause);
            videoPlayerRef.removeEventListener("paused", onPause);
            videoPlayerRef.removeEventListener("timeupdate", handleTimeUpdate);
        };
    });
    return (
        <div
            className={cx(
                styles.scrubbar,
                size === "small" && styles.small,
                expanded && styles["scrubbar--expanded"],
            )}
            ref={trackRef}
            onClick={handleTrackClick}
            onMouseEnter={handleTrackEnter}
            onMouseLeave={handleTrackLeave}
            role="presentation"
        >
            <div className={styles.scrubbar__progress} ref={progressRef} />
            <div className={styles.scrubbar__holder}>
                <div
                    className={styles.scrubbar__thumb}
                    ref={thumbRef}
                    onMouseDown={handleGrabThumb}
                    onMouseUp={handleReleaseThumb}
                    role="presentation"
                />
                {showCurrentTime && vidRef.current && (
                    <div className={styles.currentTime}>
                        {secondsToTimecode(currentTime)}
                    </div>
                )}
            </div>

            <div className={styles.scrubbar__spacer} />

            <div
                className={cx(
                    styles.mousetimecode,
                    inEditor && styles.doubleTimeCode,
                )}
                ref={timecodeRef}
            >
                {showMouseTimecode && mouseTimecode}
                {inEditor && (
                    <>
                        <br />
                        {showMouseTimecode && formattedCombinedTimestamp}
                    </>
                )}
            </div>
        </div>
    );
};

export default ScrubBar;
